/* eslint-disable */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronLeft } from 'lucide-react'
import { Button } from '../Components/ui/button'
import { Input } from '../Components/ui/input'
import { Label } from '../Components/ui/label'
import { Checkbox } from '../Components/ui/checkbox'
import env from '../env/env'
import { get, patch, post } from '../http/restservice'
import { userDetailsState } from './LoginState'
import { useRecoilState } from 'recoil'
import { useLocation, useNavigate } from 'react-router-dom'
import { cleanIndianPhoneNumber, formatIndianNumber } from '../utils/Util'
import { useModal } from '../hooks/use-modal-store'
import { useErrorPopup } from '../hooks/useErrorPopup'
import BarLoader from '../Components/BarLoader'
import { getUserDetails } from '../utils/AuthUtil'
import { v4 as uuidv4 } from 'uuid'
import PostHogTracker from '../utils/PostHogTracker'

const KycLanding = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const elgStatus = useMemo(() => {
    return new URLSearchParams(location.search).get('STATUS')
  }, [location.search])
  const [userDetails, setUserDetails] = useRecoilState(userDetailsState)
  const [checked, setChecked] = useState(false)
  const [panNumber, setPanNumber] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [loading, setLoading] = useState(false)
  const { onOpen } = useModal()
  const { showError } = useErrorPopup()
  const [showElogLoading, setShowElogLoading] = useState(false)
  const isMountedRef = useRef(true)
  const elogTimerRef = useRef(null)

  // Cleanup ref on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false
      if (elogTimerRef.current) {
        clearTimeout(elogTimerRef.current)
      }
    }
  }, [])


  //! Testing Purpose
  // useEffect(() => {
  //   // onOpen('openKycPopup', {
  //   //   title: 'One last step',
  //   //   message: `Please approve the nominee confirmation email sent to   ${userDetails?.email || 'your registered email address'} from Bombay stock exchange (BSE) to activate your account.`,
  //   //   severity: 'nominee_confirmation',
  //   //   isKycPhUpdate: false,
  //   //   handleRedirect: () => navigate('/dashboard'),
  //   // })

  //   onOpen('openKycPopup', {
  //     title: 'One last step',
  //     message: `There is one final regulatory step required before we can activate your account.`,
  //     severity: 'cvl_kra_pending',
  //     isKycPhUpdate: false,
  //     handleRedirect: () => navigate('/dashboard'),
  //   })
  // }, [])

  useEffect(() => {
    const details = getUserDetails()
    if (details && Object.keys(details).length) {
      setUserDetails(details)
      // Track KYC initiated when user lands on page
      PostHogTracker.kycInitiated()
    }
  }, [location])

  useEffect(() => {
    const elogStatus = location?.state?.isElogDone

    if (elogStatus === false && userDetails) {
      setShowElogLoading(true)
      const timer = setTimeout(() => {
        handleElog()
      }, 5000)

      return () => clearTimeout(timer) // Cleanup on unmount
    }
  }, [location?.state?.isElogDone, userDetails])

  useEffect(() => {
    const checkAndUpdateStatus = async () => {
      if (
        elgStatus?.toLowerCase() === 'success' &&
        userDetails &&
        Object.keys(userDetails).length
      ) {
        // const { kycPhone } = await fetchCustomerKycDetails(userDetails?.phoneNumber)
        const phNo = '+91' + userDetails?.phoneNumber?.replace(/^(\+91\s?)/, '')

        if (phNo) {
          updateElogStatusAndKycNumber(phNo)
        }
      } else if (elgStatus?.toLowerCase() === 'pending') {
        PostHogTracker.elogFailed('pending - awaiting BSE confirmation')
        showError(
          'BSE Authentication Pending',
          'Your KYC is complete, but we couldn’t finish the final step with BSE. Please try again after some time to complete the authentication..',
          () => navigate('/dashboard'),
        )
      } else if (elgStatus?.toLowerCase() === 'failure') {
        PostHogTracker.elogFailed('failure - BSE authentication failed')
        showError(
          'BSE Authentication Failed',
          'Your KYC is complete, but we couldn’t finish the final step with BSE. Please try again after some time to complete the authentication.',
          () => navigate('/dashboard'),
        )
      }
    }

    checkAndUpdateStatus()
  }, [location.search, userDetails])

  const handleCheckedChange = (newChecked) => {
    setChecked(newChecked)
    if (newChecked) {
      PostHogTracker.kycTermsAccepted()
    }
  }

  const handleDateChange = useCallback((e) => {
    const value = e.target.value.replace(/\D/g, '') // Remove non-digits

    // Format with slashes
    let formattedValue = ''

    if (value.length > 0) {
      formattedValue += value.substring(0, Math.min(2, value.length))
    }

    if (value.length > 2) {
      formattedValue += '/' + value.substring(2, Math.min(4, value.length))
    }

    if (value.length > 4) {
      formattedValue += '/' + value.substring(4, Math.min(8, value.length))
    }

    setDateOfBirth(formattedValue)
  }, [])

  const handlePanChange = useCallback((e) => {
    const value = e.target.value.toUpperCase()
    let formattedValue = ''

    // Remove any characters that don't match the expected pattern
    for (let i = 0; i < value.length && i < 10; i++) {
      const char = value[i]

      if (i < 5) {
        // First 5 positions: only alphabets
        if (/[A-Z]/.test(char)) {
          formattedValue += char
        }
      } else if (i < 9) {
        // Next 4 positions: only numbers
        if (/[0-9]/.test(char)) {
          formattedValue += char
        }
      } else if (i === 9) {
        // Last position: only alphabet
        if (/[A-Z]/.test(char)) {
          formattedValue += char
        }
      }
    }

    setPanNumber(formattedValue)
  }, [])

  const isPanValid = useCallback((pan) => {
    if (pan.length !== 10) return false

    // Check first 5 characters are alphabets
    for (let i = 0; i < 5; i++) {
      if (!/[A-Z]/.test(pan[i])) return false
    }

    // Check next 4 characters are numbers
    for (let i = 5; i < 9; i++) {
      if (!/[0-9]/.test(pan[i])) return false
    }

    // Check last character is alphabet
    if (!/[A-Z]/.test(pan[9])) return false

    return true
  }, [])


  const handleCvlKra = useCallback(async (e) => {
    e?.preventDefault()
    setLoading(true)

    const updateDateOfBirthResponse = await updateDateOfBirth()
    console.log(updateDateOfBirthResponse, 'updateDateOfBirthResponse')
    const response = await post(`${env.API_URL}mutualfundservice/api/cvlKRA/panStatus`, { panNo: panNumber })

    if (response?.status === 'SUCCESS' && response?.response) {
      const parser = new DOMParser()
      const xml = parser.parseFromString(response.response, 'text/xml')

      const getText = (tag) => xml.getElementsByTagName(tag)?.[0]?.textContent || ''

      const cvlKraData = {
        panNo: getText('APP_PAN_NO'),
        name: getText('APP_NAME'),
        status: getText('APP_STATUS'),
      }


      localStorage.setItem('cvlKraData', JSON.stringify(cvlKraData))
      startKYC()
    } else {
      startKYC()
    }

  }, [panNumber, dateOfBirth])



  const startKYC = useCallback(async (e) => {
    e?.preventDefault()



    setLoading(true)
    try {
      const transactionId =
        userDetails?.userId || typeof crypto?.randomUUID === 'function' ? crypto.randomUUID() : require('uuid').v4() // Generate unique transaction ID
      const workflowId = env.HV_WORKFLOW_ID // Replace with your workflow ID


      const res = await post(`${env.API_URL}customerservice/api/hyperverge/accessToken`)

      const token = res?.token

      if (window.HyperKycConfig && window.HyperKYCModule) {
        const hyperKycConfig = new window.HyperKycConfig(token, workflowId, transactionId)

        // Conditionally add emailId
        const email = userDetails?.email

        // Build input object
        const inputs = {
          mobileNumber: formatIndianNumber(userDetails?.phoneNumber),
          panNumber: panNumber,
          dob: dateOfBirth,
          emailId: email.includes('fabits') ? 'null' : email,
        }

        // Set inputs
        hyperKycConfig.setInputs(inputs)

        // Track HyperVerge SDK launch
        PostHogTracker.kycHyperVergeLaunched(userDetails?.phoneNumber)

        window.HyperKYCModule.launch(hyperKycConfig, handler)
      } else {
        PostHogTracker.kycError('hyperverge_sdk_load', 'sdk_not_loaded', 'HyperVerge SDK not loaded properly')
        alert('HyperVerge SDK not loaded properly.')
      }
    } catch (error) {

      onOpen('openKycPopup', {
        title: 'Token Fetch Failed',
        message:
          'We were unable to fetch the required authorization token to create your investment plan. Please try again in a few minutes.',
        severity: 'failure',
        handleRedirect: () => { },
      })
    } finally {
      setLoading(false)
    }
  }, [userDetails, panNumber, dateOfBirth, onOpen])

  const handleRetry = useCallback(() => {
    if (panNumber && dateOfBirth) {
      startKYC()
    } else {
      return () => { }
    }
  }, [panNumber, dateOfBirth, startKYC])

  const triggerElogAfterDelay = useCallback(() => {
    if (!userDetails?.phoneNumber) return

    // Clear any existing timer before starting a new one
    if (elogTimerRef.current) {
      clearTimeout(elogTimerRef.current)
    }

    // Wait for 10 seconds, guarded by mounted check
    elogTimerRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        handleElog()
      }
    }, 10000)
  }, [userDetails?.phoneNumber])

  const handler = useCallback(async (HyperKycResult) => {

    switch (HyperKycResult.status) {
      // ----Incomplete workflow-----
      case 'user_cancelled':
        PostHogTracker.kycCancelled()
        onOpen('openKycPopup', {
          title: 'KYC cancelled !',
          message:
            'Looks like you have cancelled your KYC. Complete it today and take the next step toward achieving your goals.',
          severity: 'user_cancelled',
          handleRedirect: handleRetry,
        })

        break

      case 'error':
        PostHogTracker.kycError('hyperverge', 'error', 'KYC process error')
        onOpen('openKycPopup', {
          title: 'Please try again!',
          message: "We're currently experiencing an issue. Please try again after some time.",
          severity: 'failure',
          handleRedirect: () => { },
        })
        break

      // ----Complete workflow-----
      case 'auto_approved':
        PostHogTracker.kycCompleted('auto_approved', 'auto_approved')
        setShowElogLoading(true)
        triggerElogAfterDelay()
        break

      case 'auto_declined':
        PostHogTracker.kycDeclined('auto_declined - documents incorrect')
        onOpen('openKycPopup', {
          title: 'KYC Declined!',
          message:
            'Your KYC didn’t go through this time - No worries. Please contact your RM.',
          severity: 'auto_declined',
          handleRedirect: () => { },
        })

        console.warn('KYC Auto Declined:', HyperKycResult)
        break

      case 'needs_review':
        PostHogTracker.kycCompleted('needs_review', 'needs_review')
        setShowElogLoading(true)
        triggerElogAfterDelay()
        break

      default:
        console.warn('Unhandled KYC Status:', HyperKycResult.status)
        break
    }
  }, [onOpen, handleRetry, triggerElogAfterDelay])

  const fetchCustomerKycDetails = useCallback(async (phNum) => {
    const phNo = '%2b91' + phNum?.replace(/^(\+91\s?)/, '')

    try {
      const response = await get(`${env.API_URL}customerservice/api/customer?phoneNumber=${phNo}`)

      if (!response || response?.isError) {
        showError(
          'We Couldn\'t Load Your KYC Info',
          'Something went wrong while fetching your KYC details. Please try again in a few moments or reach out to support if the issue continues.',
          () => navigate('/dashboard'),
        )
        return { ucc: null, kycPhone: null }
      }

      const uid = response?.uid
      const ucc = uid?.includes('-') ? uid.split('-')[0] : null
      const kycPhone = response?.kycPhoneNumber ?? null

      if (!kycPhone) {
        onOpen('openKycPopup', {
          title: 'BSE Authentication Pending',
          message:
            'Your KYC is complete, but we couldn’t finish the final step with BSE. Please try again after some time to complete the authentication.',
          severity: 'cvl_kra_pending',
          handleRedirect: () => navigate('/dashboard'),
        })

        return { ucc, kycPhone: null }
      }

      return { ucc, kycPhone }
    } catch (error) {
      console.error('Error in fetchCustomerKycDetails:', error)

      showError(
        'Unexpected Error',
        'Something went wrong while fetching your KYC details. Please try again later.',
        () => navigate('/dashboard'),
      )
      return null
    }
  }, [navigate, showError, onOpen])

  const handleElog = useCallback(async () => {
    // const result = await fetchCustomerKycDetails(userDetails?.phoneNumber)
    // if (!result) {
    //   // showError(
    //   //   'Unable to Retrieve KYC Details',
    //   //   'We couldn’t fetch your KYC information. Please try again later.',
    //   //   () => navigate('/dashboard'),
    //   // )
    //   return
    // }

    // const { ucc, kycPhone } = result

    // if (!kycPhone) {
    //   // showError(
    //   //   'Unable to Retrieve KYC Details',
    //   //   'Please try again later or contact support.',
    //   //   () => navigate('/dashboard'),
    //   // )
    //   return
    // }

    const ucc = userDetails?.uid?.includes('-') ? userDetails?.uid?.split('-')[0] : null

    const payload = {
      clientCode: ucc?.toUpperCase(),
      loopbackUrl: `${env.LOOPBACK_URL}dashboard/start-kyc`,
      allowLoopbackMsg: 'Y',
    }

    // Track that eLog authentication has been initiated for this user
    if (ucc) {
      PostHogTracker.elogInitiated(ucc?.toUpperCase())
    }

    const response = await post(
      `${env.API_URL}mutualfundservice/api/bseStar/api/elogAuthentication`,
      payload,
    )

    // Handle specific ELOG errors
    if (
      response?.isError &&
      response?.response?.data?.errordesc?.includes('FAILED: AUTHENTICATION IS ALREADY DONE')
    ) {
      PostHogTracker.elogCompleted('already_done')
      onOpen('openKycPopup', {
        title: 'Authentication Already Completed!',
        message: 'Your BSE account authentication is already done.',
        severity: 'auto_approved',
        handleRedirect: () => navigate('/dashboard'),
      })
      return
    }

    if (response?.isError) {
      PostHogTracker.elogFailed(response?.response?.data?.errordesc || 'Authentication failed')
      onOpen('openKycPopup', {
        title: 'Please try again!',
        message: 'Your account confirmation wasn\'t successful. Please try again later.',
        severity: 'failure',
        handleRedirect: () => { },
      })
      return
    }

    if (
      !response?.isError &&
      response?.status === 'SUCCESS' &&
      response?.data?.errordesc?.includes('ELOG Link Generated Successfully')
    ) {
      PostHogTracker.elogAuthenticationStarted(response?.data?.authurl)
      window.open(response?.data?.authurl, '_self')
      // setShowElogLoading(false)
    }
  }, [userDetails?.phoneNumber, onOpen, navigate])

  const updateElogStatusAndKycNumber = useCallback(async (phNo) => {
    const payload = {
      kycPhoneNumber: phNo,
      isElogDone: elgStatus === 'SUCCESS' ? true : false,
    }

    const response = await post(
      `${env.API_URL}customerservice/api/customer/updateElogStatus`,
      payload,
    )

    if (response?.isError) {
      onOpen('openKycPopup', {
        title: '',
        message: 'We were unable to process your request at the moment. Please try again later.',
        severity: 'failure',
        handleRedirect: () => navigate('/dashboard'),
      })

      return
    }

    // const isPhoneNoSame =
    //   cleanIndianPhoneNumber(phNo) === cleanIndianPhoneNumber(userDetails?.phoneNumber)
    //     ? false
    //     : true

    // if (!isPhoneNoSame) {

    let cvlKraData = null
    try {
      cvlKraData = JSON.parse(localStorage.getItem('cvlKraData'))
    } catch {
      console.error('Failed to parse cvlKraData from localStorage')
    }

    if (String(cvlKraData?.status) !== '007') {
      onOpen('openKycPopup', {
        title: 'One last step',
        message: `There is one final regulatory step required before we can activate your account.`,
        severity: 'cvl_kra_pending',
        isKycPhUpdate: false,
        handleRedirect: () => navigate('/dashboard'),
      })

    } else {

      onOpen('openKycPopup', {
        title: 'One last step',
        message: `Please approve the nominee confirmation email sent to   ${userDetails?.email || 'your registered email address'} from Bombay stock exchange (BSE) to activate your account.`,
        severity: 'nominee_confirmation',
        isKycPhUpdate: false,
        handleRedirect: () => navigate('/dashboard'),
      })
    }
  }, [elgStatus, onOpen, navigate])

  // if (isPhoneNoSame && !response?.isError) {
  //   const updateKycNumber = await post(
  //     `${env.API_URL}customerservice/api/customer/updatePrimaryPhoneNumber`,
  //     {
  //       phoneNumber: userDetails?.phoneNumber,
  //       kycPhoneNumber: phNo,
  //     },
  //   )

  //   if (updateKycNumber?.isError) {
  //     onOpen('openKycPopup', {
  //       title: 'Update Failed',
  //       message:
  //         'We were unable to update your phone number and confirm your account. Please try again later.',
  //       severity: 'failure',
  //       handleRedirect: () => navigate('/dashboard'),
  //     })
  //   }

  //   if (!updateKycNumber?.isError) {
  //     onOpen('openKycPopup', {
  //       title: 'Account Approved',
  //       message:
  //         'Your BSE authentication is already complete, and your phone number has been updated successfully.',
  //       severity: 'auto_approved',
  //       isKycPhUpdate: true,
  //       kycPhoneNumber: phNo,
  //       handleRedirect: () => navigate('/dashboard'),
  //     })
  //   }
  // }
  // }

  const updateDateOfBirth = useCallback(async () => {
    const response = await patch(
      `${env.API_URL}customerservice/api/customer/update`,
      {
        dob: dateOfBirth,
      },
    )

    return response
  }, [dateOfBirth])


  // Check if form is valid
  const isFormValid = isPanValid(panNumber) && dateOfBirth.length === 10 // DD/MM/YYYY = 10 characters

  return (
    <>
      {showElogLoading ? (
        <div className='flex flex-col space-y-10 justify-center items-center h-screen px-20 text-center'>
          <BarLoader />
          <h1 className='font-semibold text-xl leading-7 tracking-[-4%]'>
            Approve your account with Bombay Stock Exchange
          </h1>
        </div>
      ) : (
        <div className='flex items-center justify-center md:p-4'>
          <div className='h-[calc(100vh-100px)] flex flex-col w-full lg:max-w-md bg-white lg:rounded-lg lg:shadow-sm lg:border'>
            {/* Header */}
            <div
              className='flex items-center font-semibold text-black-500 p-4 border-b cursor-pointer'
              onClick={() => navigate('/dashboard')}
            >
              <ChevronLeft className='h-5 w-5  mr-3' />
              <h1>Complete KYC</h1>
            </div>

            {/* Content */}
            <div className='flex-1 flex flex-col justify-center p-6 space-y-6'>
              <div className='space-y-4'>
                {/* Coin Image */}
                <div className='flex justify-center mt-10'>
                  <div className='relative'>
                    <img
                      className='h-18 w-18'
                      src={`${env.S3_BASE_URL}/firstTime/question4.svg`}
                      alt={'start kyc'}
                    />
                  </div>
                </div>

                {/* Title and Description */}
                <div className='text-center space-y-2'>
                  <h2 className='text-xl font-semibold text-black-500'>Enter PAN number</h2>
                  <p className='text-sm text-black-400'>
                    Please provide the necessary details to set up your KYC
                  </p>
                </div>
              </div>

              <div className='space-y-2'>
                <Label htmlFor='pan-number' className='text-sm text-black-300'>
                  Pan card number <span className='text-error-500'>*</span>
                </Label>
                <Input
                  id='pan-number'
                  type='text'
                  value={panNumber}
                  onChange={handlePanChange}
                  className='w-full px-3 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-transparent'
                  placeholder='Enter PAN number'
                  required
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='date-of-birth' className='text-sm text-black-300'>
                  Date of birth <span className='text-error-500'>*</span>
                </Label>
                <Input
                  id='date-of-birth'
                  type='text'
                  value={dateOfBirth}
                  onChange={handleDateChange}
                  className='w-full px-3 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-transparent'
                  placeholder='DD-MM-YYYY'
                  required
                  maxLength={10}
                  inputMode='numeric'
                />
              </div>
            </div>

            <div className='flex items-center space-x-2 px-5 py-4'>
              {/* <Checkbox id='terms' checked={checked} onCheckedChange={handleCheckedChange} /> */}
              <Label
                htmlFor='terms'
                className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
              >
                By proceeding, you agree to the{' '}
                <a href='#' className='underline underline-offset-4 hover:text-primary'>
                  Terms and Conditions.
                </a>
              </Label>
            </div>

            <div className='flex-shrink-0 p-4'>
              {/* Form */}
              <form onSubmit={handleCvlKra} className='space-y-4'>
                <Button
                  loading={loading}
                  disabled={!isFormValid}
                  type='submit'
                  className='w-full bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-medium transition-colors disabled:bg-primary-300 disabled:cursor-not-allowed'
                >
                  Submit
                </Button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default KycLanding
